import { Link } from 'react-router-dom';
import { ArrowRight, Award, Globe, CheckCircle, ExternalLink, Shield, Building2 } from 'lucide-react';
import { SEO, pageSEO } from '../components/SEO';

const accreditations = [
  {
    name: 'National Commission on Higher Education (NCHE)',
    country: 'Liberia',
    description: 'CIU is fully accredited by the NCHE, the official government body responsible for accrediting all tertiary institutions in Liberia.',
    url: 'http://www.nche.gov.lr/content/private-tertiary-institutions',
    logo: null,
    type: 'National Accreditation',
  },
  {
    name: 'International Academic and Management Association (IAMA)',
    country: 'India',
    description: 'CIU is a recognized member of IAMA, an international body promoting academic excellence and management education globally.',
    url: 'https://iama-india.org/Partner.html',
    logo: null,
    type: 'International Recognition',
  },
  {
    name: 'Liberia Business Registry',
    country: 'Liberia',
    description: 'CIU is legally registered and operates as a recognized educational institution under Liberian law.',
    url: null,
    logo: null,
    type: 'Legal Registration',
  },
];

const benefits = [
  {
    title: 'Government Recognized',
    description: 'All programs are approved by the Ministry of Education and NCHE.',
    icon: Building2,
  },
  {
    title: 'International Standards',
    description: 'Curriculum aligned with global best practices in higher education.',
    icon: Globe,
  },
  {
    title: 'Quality Assurance',
    description: 'Regular reviews ensure maintained standards of excellence.',
    icon: Shield,
  },
  {
    title: 'Transferable Credits',
    description: 'Credits recognized by partner institutions worldwide.',
    icon: Award,
  },
];

export default function Accreditation() {
  return (
    <div data-testid="accreditation-page" className="min-h-screen pt-20">
      <SEO {...pageSEO.accreditation} url="/accreditation" />
      {/* Hero Section */}
      <section className="relative py-24 bg-[#0D4A2E] pattern-overlay">
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8">
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-4">
            Accreditation & <span className="text-[#C5A028]">Recognition</span>
          </h1>
          <p className="text-white/70 text-xl max-w-2xl">
            CEPRES International University is recognized and accredited by national 
            and international bodies, ensuring the quality and validity of your degree.
          </p>
        </div>
      </section>

      {/* Why Accreditation Matters */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="text-3xl font-bold text-[#0D4A2E] mb-6">
              Why Accreditation <span className="text-[#C5A028]">Matters</span>
            </h2>
            <p className="text-[#666666] mb-4 leading-relaxed">
              Accreditation is a validation of quality that assures students, employers, 
              and the public that an institution meets established standards of excellence 
              in education.
            </p>
            <p className="text-[#666666] leading-relaxed">
              When you earn a degree from an accredited institution like CIU, you can be 
              confident that your qualification will be recognized by employers, professional 
              bodies, and other educational institutions both locally and internationally.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-12 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="p-6 bg-white border border-[#E5E5E5] hover:border-[#C5A028] transition-colors duration-300"
              >
                <benefit.icon className="w-10 h-10 text-[#C5A028] mb-4" />
                <h3 className="font-bold text-[#0D4A2E] mb-2">{benefit.title}</h3>
                <p className="text-[#666666] text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Accreditation Bodies */}
      <section data-testid="accreditation-bodies" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <h2 className="text-3xl font-bold text-[#0D4A2E] mb-4">Our Accreditations</h2>
          <p className="text-[#666666] mb-12 max-w-2xl">
            CIU is recognized and accredited by the following bodies:
          </p>

          <div className="space-y-6">
            {accreditations.map((accreditation, index) => (
              <div 
                key={index}
                className="bg-[#F5F5F5] p-8 border-l-4 border-[#C5A028] hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                  <div className="flex-grow">
                    <span className="inline-block px-3 py-1 bg-[#0D4A2E] text-white text-xs font-semibold mb-3">
                      {accreditation.type}
                    </span>
                    <h3 className="text-xl font-bold text-[#0D4A2E] mb-2">
                      {accreditation.name}
                    </h3>
                    <p className="text-[#C5A028] text-sm font-medium mb-3">{accreditation.country}</p>
                    <p className="text-[#666666]">{accreditation.description}</p>
                  </div>
                  {accreditation.url && (
                    <a 
                      href={accreditation.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-[#0D4A2E] text-white font-semibold hover:bg-[#082D1C] transition-colors duration-200 flex-shrink-0"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Verify
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What This Means for You */}
      <section className="py-20 bg-[#0D4A2E] text-white pattern-overlay">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">
                What This Means <span className="text-[#C5A028]">For You</span>
              </h2>
              <p className="text-white/80 mb-8 leading-relaxed">
                As a CIU graduate, your degree is:
              </p>
              <ul className="space-y-4">
                {[
                  'Recognized by employers across Liberia and internationally',
                  'Valid for further studies at other accredited institutions',
                  'Accepted by professional licensing bodies',
                  'Backed by a quality-assured curriculum',
                  'Supported by qualified and experienced faculty',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-[#C5A028] flex-shrink-0 mt-0.5" />
                    <span className="text-white/80">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white/5 border border-white/10 p-8 text-center">
              <Award className="w-20 h-20 text-[#C5A028] mx-auto mb-6" />
              <h3 className="text-2xl font-bold mb-4">NCHE Accredited</h3>
              <p className="text-white/60 mb-6">
                All 19 degree programs and 17 vocational programs are fully accredited
              </p>
              <Link 
                to="/programs"
                className="inline-flex items-center gap-2 bg-[#C5A028] text-[#1A1A1A] px-6 py-3 font-bold hover:bg-[#D4B43D] transition-colors duration-200"
              >
                <ArrowRight className="w-5 h-5" />
                VIEW PROGRAMS
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#F5F5F5]">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-[#0D4A2E] mb-4">Ready to Join?</h2>
          <p className="text-[#666666] mb-8">
            Start your journey with a nationally and internationally recognized degree.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="https://cnte.ciu.edu.lr/apply"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#C5A028] text-[#1A1A1A] px-8 py-4 font-bold hover:bg-[#D4B43D] transition-colors duration-200"
            >
              <ArrowRight className="w-5 h-5" />
              APPLY NOW
            </a>
            <Link 
              to="/contact"
              className="inline-flex items-center gap-2 border-2 border-[#0D4A2E] text-[#0D4A2E] px-8 py-4 font-bold hover:bg-[#0D4A2E] hover:text-white transition-colors duration-200"
            >
              CONTACT US
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
